import React from 'react';
import {
  Breadcrumbs,
  Typography,
  Link,
  Box,
  Chip,
  useTheme,
  Paper
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { seoConfig } from '../config/seoConfig';

const BreadcrumbsNavigation = ({ 
  customBreadcrumbs = null, 
  showBackground = true,
  size = 'medium' 
}) => {
  const location = useLocation();
  const theme = useTheme();
  
  // Générer les breadcrumbs automatiquement depuis l'URL
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const breadcrumbs = [
      {
        label: 'Accueil',
        path: '/',
        icon: <HomeIcon fontSize="small" />
      }
    ];

    // Logique pour différents types de pages
    if (pathnames.length === 0) {
      return breadcrumbs; // Page d'accueil uniquement
    }

    // Page métier principale (/electriciens)
    if (pathnames.length === 1) {
      const metier = pathnames[0];
      const metierConfig = seoConfig.metiers[metier];
      
      if (metierConfig) {
        breadcrumbs.push({
          label: metierConfig.title.split('|')[0].trim(),
          path: `/${metier}`,
          icon: <BusinessIcon fontSize="small" />,
          current: true,
          description: `Solutions pour ${metier}`
        });
      } else {
        // Pages standards
        const pageLabels = {
          'tarifs': { label: 'Tarifs', icon: <BusinessIcon fontSize="small" /> },
          'analytics': { label: 'Analytics', icon: <DashboardIcon fontSize="small" /> },
          'contact': { label: 'Contact', icon: <BusinessIcon fontSize="small" /> },
          'demo': { label: 'Démonstration', icon: <BuildIcon fontSize="small" /> },
          'support': { label: 'Support', icon: <BusinessIcon fontSize="small" /> }
        };
        
        const pageInfo = pageLabels[metier] || { label: metier, icon: <BusinessIcon fontSize="small" /> };
        breadcrumbs.push({
          label: pageInfo.label,
          path: `/${metier}`,
          icon: pageInfo.icon,
          current: true
        });
      }
    }

    // Page métier + ville (/electriciens-paris)
    else if (pathnames.length === 1 && pathnames[0].includes('-')) {
      const parts = pathnames[0].split('-');
      const metier = parts[0];
      const ville = parts.slice(1).join('-');
      const metierConfig = seoConfig.metiers[metier];
      
      if (metierConfig) {
        // Breadcrumb métier
        breadcrumbs.push({
          label: metierConfig.title.split('|')[0].trim(),
          path: `/${metier}`,
          icon: <BusinessIcon fontSize="small" />
        });
        
        // Breadcrumb ville
        breadcrumbs.push({
          label: ville.charAt(0).toUpperCase() + ville.slice(1).replace('-', ' '),
          path: `/${pathnames[0]}`,
          icon: <LocationIcon fontSize="small" />,
          current: true,
          description: `${metier} à ${ville}`
        });
      }
    }

    // Page service (/electriciens/installation)
    else if (pathnames.length === 2) {
      const metier = pathnames[0];
      const service = pathnames[1];
      const metierConfig = seoConfig.metiers[metier];
      
      if (metierConfig) {
        // Breadcrumb métier
        breadcrumbs.push({
          label: metierConfig.title.split('|')[0].trim(),
          path: `/${metier}`,
          icon: <BusinessIcon fontSize="small" />
        });
        
        // Breadcrumb service
        const serviceInfo = metierConfig.services?.find(s => 
          s.name.toLowerCase().replace(/\s+/g, '-') === service
        );
        
        breadcrumbs.push({
          label: serviceInfo?.name || service.replace('-', ' '),
          path: `/${metier}/${service}`,
          icon: <BuildIcon fontSize="small" />,
          current: true,
          description: serviceInfo?.description || `Service ${service}`
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Styles conditionnels selon la taille
  const styles = {
    small: {
      fontSize: '0.75rem',
      padding: '8px 12px',
      iconSize: 'small'
    },
    medium: {
      fontSize: '0.875rem',
      padding: '12px 16px',
      iconSize: 'small'
    },
    large: {
      fontSize: '1rem',
      padding: '16px 24px',
      iconSize: 'medium'
    }
  };

  const currentStyle = styles[size];

  const BreadcrumbsContent = (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
      sx={{
        '& .MuiBreadcrumbs-ol': {
          alignItems: 'center'
        }
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const isActive = crumb.current || isLast;

        return (
          <Box key={crumb.path} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Icône */}
            {crumb.icon && (
              <Box sx={{ 
                color: isActive ? theme.palette.primary.main : '#64748b',
                display: 'flex',
                alignItems: 'center'
              }}>
                {crumb.icon}
              </Box>
            )}
            
            {/* Lien ou texte */}
            {isActive ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: currentStyle.fontSize
                  }}
                >
                  {crumb.label}
                </Typography>
                {crumb.description && size !== 'small' && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      display: 'block',
                      fontSize: '0.75rem',
                      mt: 0.25
                    }}
                  >
                    {crumb.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Link
                component={RouterLink}
                to={crumb.path}
                underline="hover"
                sx={{
                  color: '#64748b',
                  fontSize: currentStyle.fontSize,
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    textDecoration: 'underline'
                  }
                }}
              >
                {crumb.label}
              </Link>
            )}
          </Box>
        );
      })}
    </Breadcrumbs>
  );

  // Avec ou sans background
  if (showBackground) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          padding: currentStyle.padding
        }}
      >
        {BreadcrumbsContent}
      </Paper>
    );
  }

  return (
    <Box sx={{ padding: currentStyle.padding }}>
      {BreadcrumbsContent}
    </Box>
  );
};

// Composant spécialisé pour les pages métiers
export const MetierBreadcrumbs = ({ metier, city = null, service = null }) => {
  const metierConfig = seoConfig.metiers[metier];
  
  if (!metierConfig) return null;

  const customBreadcrumbs = [
    {
      label: 'Accueil',
      path: '/',
      icon: <HomeIcon fontSize="small" />
    },
    {
      label: metierConfig.title.split('|')[0].trim(),
      path: `/${metier}`,
      icon: <BusinessIcon fontSize="small" />,
      current: !city && !service
    }
  ];

  if (city) {
    customBreadcrumbs.push({
      label: city,
      path: `/${metier}-${city.toLowerCase().replace(/\s+/g, '-')}`,
      icon: <LocationIcon fontSize="small" />,
      current: !service,
      description: `${metier} à ${city}`
    });
  }

  if (service) {
    const serviceSlug = service.toLowerCase().replace(/\s+/g, '-');
    customBreadcrumbs.push({
      label: service,
      path: `/${metier}/${serviceSlug}`,
      icon: <BuildIcon fontSize="small" />,
      current: true,
      description: `Service spécialisé`
    });
  }

  return <BreadcrumbsNavigation customBreadcrumbs={customBreadcrumbs} />;
};

// Composant avec suggestions de navigation
export const SmartBreadcrumbs = ({ suggestions = true }) => {
  const location = useLocation();
  
  const getSuggestions = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    
    if (pathnames.length === 1) {
      const metier = pathnames[0];
      const metierConfig = seoConfig.metiers[metier];
      
      if (metierConfig && metierConfig.cities) {
        return {
          title: 'Également disponible dans ces villes :',
          items: metierConfig.cities.slice(0, 4).map(city => ({
            label: city.name,
            path: `/${metier}-${city.name.toLowerCase().replace(/\s+/g, '-')}`,
            icon: <LocationIcon fontSize="small" />
          }))
        };
      }
    }
    
    return null;
  };

  const suggestionData = suggestions ? getSuggestions() : null;

  return (
    <Box>
      <BreadcrumbsNavigation />
      
      {suggestionData && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {suggestionData.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {suggestionData.items.map((item, index) => (
              <Chip
                key={index}
                label={item.label}
                component={RouterLink}
                to={item.path}
                clickable
                size="small"
                icon={item.icon}
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BreadcrumbsNavigation;